import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, CircularProgress, Box, Paper,
} from "@material-ui/core";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import BulkImportResult from "./BulkImportResult";

const STATUS_COLORS = {
  pending: "#757575",
  processing: "#1976d2",
  completed: "#2e7d32",
  paused_meta: "#ed6c02",
  failed: "#d32f2f",
  cancelled: "#757575",
};

const statusLabel = (st) => i18n.t(`bulkSend.result.${st === "paused_meta" ? "pausedMeta" : st}`) || st;

const useStyles = makeStyles((theme) => ({
  root: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", padding: theme.spacing(2) },
  detailRoot: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" },
  tableWrap: { flex: 1, minHeight: 0, overflow: "auto" },
  row: { cursor: "pointer" },
  footer: { display: "flex", justifyContent: "center", padding: theme.spacing(1), flexShrink: 0 },
  empty: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: theme.palette.text.secondary },
}));

// feat_210 Fase C-bis — historial de BulkImports de la empresa. Sin esto,
// cualquier envío completado quedaba "perdido" al salir de /carga-de-datos.
const BulkSendHistory = ({ companyId }) => {
  const classes = useStyles();
  const [imports, setImports] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const fetchPage = useCallback(async (pageNumber, append) => {
    setLoading(true);
    try {
      const { data } = await api.get("/bulk-send/imports", { params: { pageNumber } });
      setImports((prev) => (append ? [...prev, ...(data.imports || [])] : (data.imports || [])));
      setHasMore(!!data.hasMore);
      setPage(pageNumber);
    } catch (e) {
      toastError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(1, false); }, [fetchPage]);

  if (selectedId) {
    return (
      <div className={classes.detailRoot}>
        <Box flexShrink={0} marginBottom={1}>
          <Button size="small" onClick={() => setSelectedId(null)}>{i18n.t("bulkSend.history.backToList")}</Button>
        </Box>
        <BulkImportResult
          importId={selectedId}
          companyId={companyId}
          onBack={() => setSelectedId(null)}
          backLabelKey="bulkSend.history.backToList"
        />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h6" gutterBottom>{i18n.t("bulkSend.history.title")}</Typography>
      {loading && imports.length === 0 ? (
        <Box display="flex" justifyContent="center" padding={4}><CircularProgress /></Box>
      ) : imports.length === 0 ? (
        <div className={classes.empty}>{i18n.t("bulkSend.history.empty")}</div>
      ) : (
        <>
          <Paper variant="outlined" className={classes.tableWrap}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("bulkSend.history.columnDate")}</TableCell>
                  <TableCell>{i18n.t("bulkSend.history.columnFile")}</TableCell>
                  <TableCell>{i18n.t("bulkSend.history.columnStatus")}</TableCell>
                  <TableCell>{i18n.t("bulkSend.history.columnProgress")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {imports.map((imp) => (
                  <TableRow key={imp.id} hover className={classes.row} onClick={() => setSelectedId(imp.id)}>
                    <TableCell>{imp.createdAt ? new Date(imp.createdAt).toLocaleString() : "—"}</TableCell>
                    <TableCell>{imp.filename}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel(imp.status)}
                        style={{ background: STATUS_COLORS[imp.status] || "#757575", color: "#fff" }}
                      />
                    </TableCell>
                    <TableCell>{imp.rowsSent}/{imp.rowsTotal}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <div className={classes.footer}>
            {hasMore && (
              <Button size="small" onClick={() => fetchPage(page + 1, true)} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : i18n.t("bulkSend.history.loadMore")}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BulkSendHistory;
